import "react-resizable/css/styles.css";
import "@livekit/components-styles";
import "../modules/providers/video-call-provider/live-kit.scss";
import "../app/index.css";
import "../app/quill.css";

import React, { useEffect } from "react";
import { ApolloProvider } from "@apollo/client";
import { ChakraProvider } from "@chakra-ui/react";
import { MotionConfig } from "framer-motion";
import { withLDProvider } from "launchdarkly-react-client-sdk";
import { NextComponentType } from "next";
import type { AppProps } from "next/app";
import mixpanel from "mixpanel-browser";
import {
  NEXT_PUBLIC_LAUNCHDARKLY_SDK_CLIENT_SIDE_ID,
  NEXT_PUBLIC_MIXPANEL_TOKEN,
} from "app/client";
import Providers from "modules/providers";
import { IndexDbProvider } from "modules/providers/index-db-provider";
import { CHAKRA_PORTAL_HOST_Z_INDEX } from "../modules/providers/chakra-provider/chakra-portal-stack";
import { chakraTheme } from "../modules/providers/chakra-provider/chakra-theme";
import appGraphqlClient from "../app/app-graphql-client";
import Listeners from "modules/listeners";
import useApp from "modules/hooks/use-app";
// referenced here (not only in _document) so the next/font CSS survives
// production tree-shaking into the client bundle; _document applies the same
// variable classes to <html> so the font variables also reach Chakra portals
// rendered outside the app tree
import { inter, outfit } from "app/fonts";
import MobilePage from "./mobile";
import MaintenancePage from "./maintenance";
import UnsubscribePage from "modules/unsubscribe/unsubscribe-page";
import LeftNavLayout from "modules/messaging/left-nav/left-nav-layout";
import useMentionResolver from "modules/hooks/use-mention-resolver";
import InvitePage from "./invite";

const FONT_VARIABLE_CLASSES = `${inter.variable} ${outfit.variable}`;

function App({ Component, pageProps }: AppProps) {
  const { isMaintenanceMode, isMobile, isInvite, isUnsubscribe } = useApp();

  //Need to call here rather than in the provider to ensure that Mixpanel is initialized as early as possible
  useEffect(() => {
    mixpanel.init(NEXT_PUBLIC_MIXPANEL_TOKEN);
  }, []);

  if (isInvite) {
    return (
      <ChakraProvider theme={chakraTheme}>
        <InvitePage />
      </ChakraProvider>
    );
  }

  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  if (isMobile) {
    return <MobilePage />;
  }

  if (isUnsubscribe) {
    return <UnsubscribePage />;
  }

  return (
    <div className={FONT_VARIABLE_CLASSES} style={{ display: "contents" }}>
      <MotionConfig reducedMotion="user">
        <IndexDbProvider>
          <ApolloProvider client={appGraphqlClient}>
            <ChakraProvider
              theme={chakraTheme}
              portalZIndex={CHAKRA_PORTAL_HOST_Z_INDEX}
            >
              <Providers>
                <Layout Component={Component} pageProps={pageProps} />
                <Listeners />
              </Providers>
            </ChakraProvider>
          </ApolloProvider>
        </IndexDbProvider>
      </MotionConfig>
    </div>
  );
}

const Layout = ({
  Component,
  pageProps,
}: {
  Component: NextComponentType & { prefersFullScreenLayout?: boolean };
  pageProps: any;
}) => {
  useMentionResolver();

  if (Component.prefersFullScreenLayout) {
    return <Component {...pageProps} />;
  }

  return (
    <LeftNavLayout>
      <Component {...pageProps} />
    </LeftNavLayout>
  );
};

export default withLDProvider<AppProps & React.Attributes>({
  clientSideID: NEXT_PUBLIC_LAUNCHDARKLY_SDK_CLIENT_SIDE_ID,
})(App);
